import { useCallback, FC } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { GET_ONE_QUESTION, UPDATE_QUESTION, GET_QUESTION_LOCATION } from 'queries/question'
import {
  findOneQuestion,
  findOneQuestionVariables,
  findOneQuestion_findOneQuestion as Question,
} from '__generatedTypes__/findOneQuestion'
import { CreateQuestionInput } from '__generatedTypes__/globalTypes'
import { updateQuestion, updateQuestionVariables } from '__generatedTypes__/updateQuestion'
import { findQuestionLocation, findQuestionLocationVariables } from '__generatedTypes__/findQuestionLocation'
import { useHistory } from 'hooks/useHistory'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { Form } from '../Form'

interface IProps {
  questionEditId?: string
  onSubmit?: (question?: Question) => void
  disableDeleteButton?: boolean
}

export const Edit: FC<IProps> = ({ questionEditId: questionIdProp, onSubmit, disableDeleteButton = false }) => {
  const { questionId: questionIdParam = '' } = useParams()
  const questionId = questionIdProp ?? questionIdParam
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [update] = useMutation<updateQuestion, updateQuestionVariables>(UPDATE_QUESTION)
  const { data, loading, refetch } = useQuery<findOneQuestion, findOneQuestionVariables>(GET_ONE_QUESTION, {
    variables: {
      params: questionId,
    },
    fetchPolicy: 'network-only',
  })
  const question = data?.findOneQuestion
  const { data: questionLocationData, loading: loadingQuestionLocation } = useQuery<
    findQuestionLocation,
    findQuestionLocationVariables
  >(GET_QUESTION_LOCATION, {
    variables: {
      id: questionId,
    },
    fetchPolicy: 'network-only',
  })
  const questionLocations = questionLocationData?.findQuestionLocation?.location

  const handleSubmit = useCallback(
    async (question: CreateQuestionInput) => {
      try {
        await update({
          variables: {
            params: {
              id: questionId,
              ...question,
              history: populateHistoryWithUser(question.history),
            },
          },
        })

        await refetch().then(({ data }) => onSubmit?.(data.findOneQuestion ?? undefined))

        toast.success('Question updated successfully')
      } catch (error) {
        toast.error('Failed to update question')
      }
    },
    [questionId, questionId, populateHistoryWithUser, update, refetch, onSubmit]
  )

  if (loading || loadingQuestionLocation || loadingHistory) {
    return <PageLoader />
  }

  if (!question) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>Question not found</h1>
        </div>
      </div>
    )
  }

  return (
    <Form
      question={question}
      onSubmit={handleSubmit}
      questionLocations={questionLocations}
      disableDeleteButton={disableDeleteButton}
    />
  )
}
